import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/ui-kit";
import { toast, Toaster } from "sonner";
import { CalendarClock, CalendarPlus, MapPin, Phone, ChevronRight } from "lucide-react";

const TYPE_COLOR = {
  "Physique": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Téléphone": "bg-sky-50 text-sky-700 border-sky-200",
  "Visio": "bg-violet-50 text-violet-700 border-violet-200",
  "Sur site": "bg-teal-50 text-teal-700 border-teal-200",
};

export default function RdvPlanifies() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);

  useEffect(() => { api.get("/rdv-planifies").then((r) => setList(r.data)).catch(() => setList([])); }, []);

  const downloadIcs = async (id, nom) => {
    try {
      const res = await api.get(`/prospects/${id}/rdv.ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rdv-${nom || "client"}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Événement téléchargé — ajoute-le à ton agenda 📅", { duration: 5000 });
    } catch (e) { toast.error("Impossible de générer l'agenda pour ce RDV."); }
  };

  return (
    <div className="p-8" data-testid="rdv-planifies-page">
      <Toaster position="top-right" richColors />
      <PageHeader title="RDV planifiés" subtitle="Du plus proche au plus lointain — organise ton agenda" />

      {list === null ? (
        <div className="p-10 text-center text-slate-500">Chargement…</div>
      ) : list.length === 0 ? (
        <EmptyState icon={CalendarClock} title="Aucun RDV à venir" description="Planifie un rendez-vous depuis une fiche prospect." testid="rdv-empty" />
      ) : (
        <div className="space-y-3" data-testid="rdv-list">
          {list.map((r) => (
            <div key={r.id} className="flex items-center gap-4 bg-white border border-slate-200 rounded-sm p-4 hover:border-[#B8873A]/50 transition-colors" data-testid={`rdv-row-${r.id}`}>
              <div className="w-20 shrink-0 text-center">
                <div className="text-sm font-bold text-[#111111]">{formatDate(r.date_prochain_rdv)}</div>
                {r.heure_prochain_rdv && <div className="text-lg font-bold text-[#B8873A] leading-none mt-0.5">{r.heure_prochain_rdv}</div>}
              </div>
              <div className="w-px h-10 bg-slate-100 shrink-0" />
              <button onClick={() => navigate(`/prospects/${r.id}`)} className="flex-1 min-w-0 text-left group">
                <div className="font-semibold text-[#111111] group-hover:text-[#B8873A] truncate">
                  {r.prenom} {r.nom}
                  <ChevronRight className="w-3.5 h-3.5 inline-block ml-0.5 -mt-0.5 opacity-0 group-hover:opacity-100" />
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                  {r.programme && <span className="truncate">{r.programme}</span>}
                  {(r.ville || r.code_postal) && <span className="flex items-center gap-1 shrink-0"><MapPin className="w-3 h-3" />{[r.code_postal, r.ville].filter(Boolean).join(" ")}</span>}
                  {r.telephone && <span className="flex items-center gap-1 shrink-0"><Phone className="w-3 h-3" />{r.telephone}</span>}
                </div>
              </button>
              {r.type_rdv && (
                <span className={`text-[10px] font-semibold px-2 py-1 rounded-full border shrink-0 ${TYPE_COLOR[r.type_rdv] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                  {r.type_rdv}{r.niveau_rdv ? ` · ${r.niveau_rdv}` : ""}
                </span>
              )}
              <button onClick={() => downloadIcs(r.id, r.nom)} data-testid={`rdv-ics-${r.id}`}
                className="shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
                <CalendarPlus className="w-3.5 h-3.5" /> Agenda
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
